import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, LogOut, Mail, Phone, MapPin, Loader2, Filter, ArrowUpDown, Image as ImageIcon,
  ChevronRight, Inbox as InboxIcon, X, ExternalLink,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { fetchAdminQuotes, fetchAdminStats, updateAdminQuote, API } from "@/lib/api";

const STATUS_META = {
  new: { label: "New", color: "bg-primary text-primary-foreground" },
  quoted: { label: "Quoted", color: "bg-amber-500 text-white" },
  booked: { label: "Booked", color: "bg-blue-600 text-white" },
  done: { label: "Done", color: "bg-emerald-600 text-white" },
  cancelled: { label: "Cancelled", color: "bg-muted-foreground text-white" },
};

const STATUSES = ["new", "quoted", "booked", "done", "cancelled"];

export default function AdminInbox() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, quoted: 0, booked: 0, done: 0, cancelled: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [rows, s] = await Promise.all([
        fetchAdminQuotes(token, { status: statusFilter, sort: sortBy, order }),
        fetchAdminStats(token),
      ]);
      setQuotes(rows);
      setStats(s);
    } catch (err) {
      toast.error("Couldn't load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, sortBy, order]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const updateQuote = async (id, patch) => {
    try {
      const updated = await updateAdminQuote(token, id, patch);
      setQuotes((rows) => rows.map((r) => (r.id === id ? updated : r)));
      if (selected?.id === id) setSelected(updated);
      // refresh stats
      fetchAdminStats(token).then(setStats).catch(() => {});
      toast.success("Updated");
    } catch (err) {
      toast.error("Update failed");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-inbox-page">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-secondary text-secondary-foreground border-b border-black/20 grain-overlay">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between relative">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-logo">
            <span className="w-8 h-8 rounded-sm bg-primary text-primary-foreground grid place-items-center">
              <Camera size={16} strokeWidth={2.4} />
            </span>
            <span className="font-display font-bold">
              Snap<span className="text-primary">.</span>Send<span className="text-primary">.</span>Repair
            </span>
            <span className="ml-3 text-xs uppercase tracking-widest text-white/60">Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs text-white/60" data-testid="admin-user-email">{user?.email}</span>
            <a href="/" target="_blank" rel="noreferrer" className="text-xs text-white/70 hover:text-primary flex items-center gap-1" data-testid="view-site">
              View site <ExternalLink size={12} />
            </a>
            <button onClick={handleLogout} className="text-xs text-white/70 hover:text-primary flex items-center gap-1" data-testid="admin-logout">
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Header row */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest text-primary mb-2">Quote requests</div>
            <h1 className="font-display text-4xl lg:text-5xl font-bold text-secondary leading-none">
              The inbox.
            </h1>
            <p className="mt-3 text-muted-foreground text-sm">
              {loading ? "Loading…" : `${quotes.length} request${quotes.length === 1 ? "" : "s"} shown · ${stats.total} total`}
            </p>
          </div>
        </div>

        {/* Stat pills */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-8" data-testid="stats-row">
          <StatCard label="Total" value={stats.total} active={statusFilter === "all"} onClick={() => setStatusFilter("all")} testid="stat-all" />
          {STATUSES.map((s) => (
            <StatCard
              key={s}
              label={STATUS_META[s].label}
              value={stats[s] || 0}
              tone={STATUS_META[s].color}
              active={statusFilter === s}
              onClick={() => setStatusFilter(s)}
              testid={`stat-${s}`}
            />
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4 items-stretch sm:items-center bg-card border border-border rounded-sm p-3">
          <div className="flex items-center gap-2 text-xs text-muted-foreground pl-1">
            <Filter size={14} /> Filter
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="field h-auto py-2 px-3 border-0 rounded-sm sm:w-44" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground pl-1">
            <ArrowUpDown size={14} /> Sort
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="field h-auto py-2 px-3 border-0 rounded-sm sm:w-48" data-testid="sort-by">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created_at">Newest first</SelectItem>
              <SelectItem value="updated_at">Recently updated</SelectItem>
              <SelectItem value="status">Status</SelectItem>
              <SelectItem value="postcode">Postcode</SelectItem>
              <SelectItem value="name">Name</SelectItem>
            </SelectContent>
          </Select>
          <Select value={order} onValueChange={setOrder}>
            <SelectTrigger className="field h-auto py-2 px-3 border-0 rounded-sm sm:w-32" data-testid="sort-order">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">Desc</SelectItem>
              <SelectItem value="asc">Asc</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* List */}
        {loading ? (
          <div className="py-24 grid place-items-center text-muted-foreground">
            <Loader2 className="animate-spin" size={28} />
          </div>
        ) : quotes.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-border rounded-sm" data-testid="empty-state">
            <InboxIcon className="mx-auto text-muted-foreground" size={28} />
            <div className="font-display text-xl font-semibold mt-4 text-secondary">Nothing here yet</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
              When a customer submits a quote request it'll appear here. Try adjusting your filter.
            </p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="quotes-list">
            {quotes.map((q) => (
              <QuoteRow
                key={q.id}
                quote={q}
                onOpen={() => setSelected(q)}
                onStatusChange={(newStatus) => updateQuote(q.id, { status: newStatus })}
              />
            ))}
          </div>
        )}
      </main>

      <QuoteDetail
        quote={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        onSave={(patch) => selected && updateQuote(selected.id, patch)}
      />
    </div>
  );
}

function StatCard({ label, value, tone, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`text-left rounded-sm border p-4 transition-colors ${
        active ? "border-primary bg-primary/5" : "border-border bg-card hover:border-primary/50"
      }`}
    >
      <div className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${tone || "bg-muted text-muted-foreground"}`}>
        {label}
      </div>
      <div className="font-display text-3xl font-bold mt-2 text-secondary leading-none">{value}</div>
    </button>
  );
}

function QuoteRow({ quote, onOpen, onStatusChange }) {
  const meta = STATUS_META[quote.status] || STATUS_META.new;
  const created = new Date(quote.created_at).toLocaleString();
  return (
    <div
      className="group bg-card border border-border rounded-sm p-4 lg:p-5 hover:border-primary/60 transition-colors"
      data-testid={`quote-row-${quote.id}`}
    >
      <div className="grid lg:grid-cols-12 gap-4 items-start">
        <div className="lg:col-span-4">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm ${meta.color}`}>
              {meta.label}
            </span>
            <span className="text-xs text-muted-foreground">{created}</span>
          </div>
          <div className="mt-2 font-display text-lg font-semibold text-secondary">{quote.name}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{quote.service_type} · {quote.user_type}</div>
        </div>

        <div className="lg:col-span-4 text-sm text-muted-foreground space-y-1">
          <div className="flex items-center gap-2"><Mail size={13} /> <span className="truncate">{quote.email}</span></div>
          <div className="flex items-center gap-2"><Phone size={13} /> {quote.phone}</div>
          <div className="flex items-center gap-2"><MapPin size={13} /> <span className="truncate">{quote.address}, {quote.postcode}</span></div>
        </div>

        <div className="lg:col-span-4 flex flex-col sm:flex-row gap-2 lg:justify-end items-stretch sm:items-center">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <ImageIcon size={13} /> {quote.photos?.length || 0}
          </div>
          <Select value={quote.status} onValueChange={onStatusChange}>
            <SelectTrigger className="field h-auto py-2 px-3 border-0 rounded-sm sm:w-36" data-testid={`row-status-${quote.id}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <button
            onClick={onOpen}
            className="btn-lift bg-secondary text-secondary-foreground rounded-sm px-4 py-2 text-sm font-medium inline-flex items-center gap-1"
            data-testid={`row-open-${quote.id}`}
          >
            Open <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function QuoteDetail({ quote, open, onClose, onSave }) {
  const [status, setStatus] = useState(quote?.status || "new");
  const [amount, setAmount] = useState(quote?.quote_amount ?? "");
  const [notes, setNotes] = useState(quote?.admin_notes || "");

  useEffect(() => {
    setStatus(quote?.status || "new");
    setAmount(quote?.quote_amount ?? "");
    setNotes(quote?.admin_notes || "");
  }, [quote]);

  const hasChanges = useMemo(() => {
    if (!quote) return false;
    return (
      status !== quote.status ||
      (amount === "" ? quote.quote_amount != null : Number(amount) !== quote.quote_amount) ||
      (notes || "") !== (quote.admin_notes || "")
    );
  }, [status, amount, notes, quote]);

  if (!quote) return null;

  const save = () => {
    const patch = {};
    if (status !== quote.status) patch.status = status;
    if (amount !== "" && Number(amount) !== quote.quote_amount) patch.quote_amount = Number(amount);
    if (amount === "" && quote.quote_amount != null) patch.quote_amount = null;
    if ((notes || "") !== (quote.admin_notes || "")) patch.admin_notes = notes;
    if (Object.keys(patch).length) onSave(patch);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 gap-0 overflow-hidden max-h-[92vh] flex flex-col" data-testid="quote-detail">
        <DialogHeader className="p-6 pb-4 border-b border-border">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="font-display text-2xl font-bold text-secondary">
                {quote.name}
              </DialogTitle>
              <div className="text-xs text-muted-foreground mt-1">
                Ref {quote.id.slice(0, 8)} · {new Date(quote.created_at).toLocaleString()}
              </div>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-secondary" data-testid="detail-close">
              <X size={18} />
            </button>
          </div>
        </DialogHeader>

        <div className="p-6 overflow-y-auto space-y-6">
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <InfoRow label="Service">{quote.service_type}</InfoRow>
            <InfoRow label="User type">{quote.user_type}</InfoRow>
            <InfoRow label="Email"><a href={`mailto:${quote.email}`} className="text-primary hover:underline">{quote.email}</a></InfoRow>
            <InfoRow label="Phone"><a href={`tel:${quote.phone}`} className="text-primary hover:underline">{quote.phone}</a></InfoRow>
            <InfoRow label="Address" className="sm:col-span-2">{quote.address}, {quote.postcode}</InfoRow>
          </div>

          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Description</div>
            <div className="bg-muted/50 rounded-sm p-4 text-sm whitespace-pre-wrap leading-relaxed">{quote.description}</div>
          </div>

          {quote.photos?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Photos ({quote.photos.length})</div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="detail-photos">
                {quote.photos.map((p) => (
                  <a
                    key={p.id}
                    href={`${API}/photos/${p.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="aspect-square block rounded-sm overflow-hidden border border-border bg-muted hover:border-primary transition-colors"
                    data-testid={`detail-photo-${p.id}`}
                  >
                    <img src={`${API}/photos/${p.id}`} alt={p.original_filename} className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Status</span>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="field h-auto py-3 px-3 border-0 rounded-sm" data-testid="detail-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Quote amount (AUD)</span>
              <input
                type="number"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 320.00"
                className="field w-full rounded-sm px-4 py-3"
                data-testid="detail-amount"
              />
            </label>
            <label className="block sm:col-span-2">
              <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">Internal notes</span>
              <textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Team-only notes, not visible to the customer."
                className="field w-full rounded-sm px-4 py-3 resize-none"
                data-testid="detail-notes"
              />
            </label>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/40 flex items-center justify-between">
          <div className="text-xs text-muted-foreground">
            {quote.updated_at ? `Last updated ${new Date(quote.updated_at).toLocaleString()}` : "Never updated"}
          </div>
          <button
            onClick={save}
            disabled={!hasChanges}
            className="btn-lift bg-primary text-primary-foreground rounded-sm px-5 py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="detail-save"
          >
            Save changes
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InfoRow({ label, children, className = "" }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">{label}</div>
      <div className="text-sm text-secondary">{children}</div>
    </div>
  );
}
